import { Fragment, useMemo, useState } from 'react';
import { Tooltip } from 'antd';
import {
    BugOutlined,
    CustomerServiceOutlined,
    InboxOutlined,
    PaperClipOutlined,
    PullRequestOutlined,
    RiseOutlined,
    RocketOutlined,
    SafetyCertificateFilled,
    SafetyCertificateOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { GiUpgrade } from 'react-icons/gi';
import Markdown from '@/components/ui/Markdown';
import { resolveDescriptionMentionsForDisplay } from '@utils/richTextMentions';
import {
    isTaskMarkedIssueOrUpgrade,
    isTaskMarkedClientTicket,
    isTaskMarkedBlocker,
} from '@hooks/projects/projectHelpers';
import {
    BoardGroupedSubmissionCard,
    BoardGroupedSubmissionSid,
    BoardGroupedSubmissionHeading,
    BoardGroupedSubmissionRule,
    BoardGroupedSubmissionTaskRow,
    BoardGroupedSubmissionTaskId,
    BoardGroupedSubmissionTaskPreview,
} from '@/pages/inside/tenant/project/projectTabStyles';
import { Container } from '@/components/ui/Container';
import { MutedText, PanelSectionLabel } from '@/pages/inside/tenant/style';
import { UserAvatarWithRoleTooltip } from '@/components/ui/UserAvatarWithRoleTooltip';

const headerBtnReset = {
    display: 'block',
    width: '100%',
    margin: 0,
    padding: 0,
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
};

const QA_TASK_ROW_ICON_PX = 14;
const QA_TASK_ROW_ICON_HIT = 22;

const QA_ICON_CHIP = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: QA_TASK_ROW_ICON_HIT,
    minHeight: QA_TASK_ROW_ICON_HIT,
    boxSizing: 'border-box',
    lineHeight: 1,
    flexShrink: 0,
};

function getNormalizedTaskWorkType(task) {
    const raw = task?.type ?? task?.taskType ?? task?.task_type;
    return String(raw ?? 'feature').toLowerCase();
}

/**
 * All QA / footprint / type / priority icons on the right (same signals as {@link SprintTaskCard} header row).
 */
function QaSubmissionTaskRowIcons({ task }) {
    let wt = getNormalizedTaskWorkType(task);

    if (isTaskMarkedIssueOrUpgrade(task)) {
        wt = 'issueorupgrade';
    } else if (isTaskMarkedClientTicket(task)) {
        wt = 'clientticket';
    } else if (isTaskMarkedBlocker(task)) {
        wt = 'blocker';
    }
    
    let Icon = RocketOutlined;
    let color = '#7c3aed';
    let label = 'Feature';

    if (wt === 'bug' || wt === 'bugfix') {
        Icon = BugOutlined;
        color = '#E57373';
        label = 'Bug';
    } else if (wt === 'improvement') {
        Icon = RiseOutlined;
        color = '#1677ff';
        label = 'Improvement';
    } else if (wt === 'blocker') {
        Icon = WarningOutlined;
        color = '#fa8c16';
        label = 'Blocker';
    } else if (wt === 'backlog') {
        Icon = InboxOutlined;
        color = '#6366f1';
        label = 'Backlog';
    } else if (wt === 'changerequest' || wt === 'change_request') {
        Icon = PullRequestOutlined;
        color = '#722ed1';
        label = 'Change Request';
    } else if (wt === 'issueorupgrade' || wt === 'issue_upgrade') {
        Icon = GiUpgrade;
        color = '#1677ff';
        label = 'Issue/Upgrade';
    } else if (wt === 'clientticket' || wt === 'client_ticket') {
        Icon = CustomerServiceOutlined;
        color = '#13c2c2';
        label = 'Client Ticket';
    }

    return (
        <Tooltip title={label}>
            <span style={{ ...QA_ICON_CHIP, color }} aria-label={`${label} task type`}>
                <Icon style={{ fontSize: QA_TASK_ROW_ICON_PX }} aria-hidden />
            </span>
        </Tooltip>
    );
}

function SubmissionMetaRow({ attachmentCount, submittedAtLabel, submittedBy }) {
    const hasAttachments = Number(attachmentCount || 0) > 0;
    return (
        <Container $display="flex" $alignItems="center" $justifyContent="space-between" $gap="10px">
            <Container $display="flex" $alignItems="center" $gap="8px" $flexWrap="wrap" style={{ minWidth: 0 }}>
                <Container
                    $display="inline-flex"
                    $alignItems="center"
                    $gap="6px"
                    style={{
                        padding: '4px 10px',
                        borderRadius: 999,
                        border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                        background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                        fontSize: 12,
                        fontWeight: 600,
                        opacity: hasAttachments ? 1 : 0.7,
                    }}
                    aria-label={`${attachmentCount || 0} attachments`}
                >
                    <PaperClipOutlined style={{ fontSize: 12 }} />
                    <span>{attachmentCount || 0}</span>
                </Container>
            </Container>

            <Container $display="flex" $alignItems="center" $gap="10px" style={{ flexShrink: 0 }}>
                {submittedAtLabel ? (
                    <MutedText style={{ margin: 0, fontSize: 11, whiteSpace: 'nowrap' }}>{submittedAtLabel}</MutedText>
                ) : null}
                {submittedBy?.name ? (
                    <UserAvatarWithRoleTooltip
                        name={submittedBy.name}
                        roleLabel="Submitted by"
                        initials={submittedBy.initials}
                        color={submittedBy.color}
                        size={26}
                        fontSize={11}
                    />
                ) : null}
            </Container>
        </Container>
    );
}

export default function QASubmissionGroupCard({
    sidLabel = 'SUB',
    submissionTitle = 'Submission',
    submissionStatus: _submissionStatus = '',
    descriptionMarkdown = '',
    /** API payload `{ users, milestones, sprints, tasks, … }` — resolves `{{user:id}}` in notes to @mentions like task cards. */
    descriptionMentions,
    attachmentCount = 0,
    submittedAtLabel,
    submittedBy,
    /**
     * When provided (true/false), shows a QA status icon in the top-right of the card header.
     * true  → SafetyCertificateFilled (green)  "QA Passed"
     * false → SafetyCertificateOutlined (amber) "QA Pending"
     * Leave undefined (default) on regular submission cards — icon is hidden.
     */
    qaPassed,
    onOpenDetail,
    overlay = false,
    tasks = [],
    onTaskClick,
    /** Wrap each task row (e.g. QA board per-task drag). Signature: (task, index, rowElement) => node */
    decorateTaskRow,
    /**
     * dnd-kit listeners/attributes for dragging the whole submission (no visible handle).
     * Applied only to the header + description + meta strip — not task rows.
     */
    submissionDragZoneProps = null,
}) {
    const resolvedDescriptionMarkdown = useMemo(
        () =>
            resolveDescriptionMentionsForDisplay(String(descriptionMarkdown || '').trim(), descriptionMentions),
        [descriptionMarkdown, descriptionMentions],
    );
    const hasDescription = Boolean(String(resolvedDescriptionMarkdown || '').trim());
    const [hovered, setHovered] = useState(false);

    const taskList = useMemo(() => (Array.isArray(tasks) ? tasks : []), [tasks]);

    const isGenericSubmission = useMemo(() => {
        if (!submissionTitle) return true;
        const lower = submissionTitle.toLowerCase().trim();
        return (
            lower === 'submission' ||
            lower.startsWith('submission ') ||
            /^submission\s+sub-\d+/i.test(lower) ||
            /^submission\s+sid-\d+/i.test(lower)
        );
    }, [submissionTitle]);

    return (
        <BoardGroupedSubmissionCard
            className="qa-submission-group-card"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={(e) => {
                if (!onOpenDetail) return;
                onOpenDetail();
            }}
            style={{
                ...(overlay ? { boxShadow: '0 10px 28px rgba(0,0,0,0.18)' } : null),
                transition: 'box-shadow 160ms ease, border-color 160ms ease',
                boxShadow: hovered ? '0 2px 28px rgba(0,0,0,0.10)' : undefined,
                borderColor: hovered ? '#cececeff' : undefined,
                cursor: onOpenDetail ? 'pointer' : undefined,
            }}
        >
            <div
                data-qa-submission-drag-zone
                {...(submissionDragZoneProps || {})}
                style={{
                    width: '100%',
                    ...(Object.keys(submissionDragZoneProps || {}).length > 0
                        ? { cursor: 'grab', touchAction: 'none' }
                        : {}),
                }}
            >
                <Container $display="flex" $alignItems="flex-start" $gap="8px" style={{ width: '100%' }}>
                    <button
                        type="button"
                        style={{
                            ...headerBtnReset,
                            cursor: onOpenDetail ? 'pointer' : headerBtnReset.cursor,
                            flex: 1,
                            minWidth: 0,
                        }}
                        aria-label={`${sidLabel}${isGenericSubmission ? '' : `, ${submissionTitle}`}. ${onOpenDetail ? 'View submission details.' : ''}`}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onOpenDetail?.();
                        }}
                    >
                        <BoardGroupedSubmissionSid as="span" style={{ marginBottom: isGenericSubmission ? 0 : 4 }}>
                            {sidLabel}
                        </BoardGroupedSubmissionSid>
                        {!isGenericSubmission && (
                            <BoardGroupedSubmissionHeading as="span">{submissionTitle}</BoardGroupedSubmissionHeading>
                        )}
                    </button>
                    {/* QA status icon — top-right, icon-only with tooltip (consultation cards only) */}
                    {qaPassed !== undefined && qaPassed !== null ? (
                        <Tooltip title={qaPassed ? 'QA Passed' : 'QA Pending'}>
                            <span
                                aria-label={qaPassed ? 'QA Passed' : 'QA Pending'}
                                style={{
                                    ...QA_ICON_CHIP,
                                    color: qaPassed ? '#52c41a' : '#fa8c16',
                                    flexShrink: 0,
                                    marginTop: 2,
                                }}
                            >
                                {qaPassed
                                    ? <SafetyCertificateFilled style={{ fontSize: 18 }} />
                                    : <SafetyCertificateOutlined style={{ fontSize: 18 }} />}
                            </span>
                        </Tooltip>
                    ) : null}
                </Container>

                <div style={{ marginTop: 10 }}>
                    <PanelSectionLabel style={{ marginBottom: 6 }}>Submission Notes</PanelSectionLabel>
                    {hasDescription ? (
                        <div
                            onClick={(e) => {
                                const el = e.target;
                                if (el && el.closest && el.closest('a')) return;
                                e.preventDefault?.();
                                e.stopPropagation();
                                onOpenDetail?.();
                            }}
                        >
                            <Markdown source={resolvedDescriptionMarkdown} variant="compact" lineClamp={3} linkPrimary richInlineAnchors />
                        </div>
                    ) : (
                        <div style={{ fontSize: 13, color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))' }}>—</div>
                    )}
                </div>

                <div style={{ marginTop: 10 }}>
                    <SubmissionMetaRow
                        attachmentCount={attachmentCount}
                        submittedAtLabel={submittedAtLabel}
                        submittedBy={submittedBy}
                    />
                </div>
            </div>

            <BoardGroupedSubmissionRule />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                {taskList.map((t, idx) => {
                    const taskNum = t?.taskNumber ?? t?.task_number;
                    let displayNumber = '';
                    if (taskNum != null && String(taskNum).trim() !== '') {
                        const s = String(taskNum).trim();
                        if (/^[A-Za-z]+-/.test(s)) {
                            displayNumber = s;
                        } else {
                            displayNumber = `T-${s}`;
                        }
                    }
                    const key = displayNumber || t?.taskId || t?.id || `t-${idx}`;
                    const title = t?.title ?? '—';
                    const rowInner = (
                        <BoardGroupedSubmissionTaskRow
                            type="button"
                            aria-label={`${key}, ${title}`}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onTaskClick?.(t);
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 12,
                                width: '100%',
                            }}
                        >
                            <div
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 4,
                                }}
                            >
                                <BoardGroupedSubmissionTaskId as="span" style={{ maxWidth: '100%' }}>
                                    {displayNumber}
                                </BoardGroupedSubmissionTaskId>
                                <BoardGroupedSubmissionTaskPreview
                                    as="span"
                                    style={{ WebkitLineClamp: 2, alignSelf: 'stretch', textAlign: 'left' }}
                                >
                                    {title}
                                </BoardGroupedSubmissionTaskPreview>
                            </div>
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                    alignSelf: 'flex-start',
                                    marginTop: 0,
                                }}
                            >
                                <QaSubmissionTaskRowIcons task={t} />
                            </div>
                        </BoardGroupedSubmissionTaskRow>
                    );
                    return (
                        <Fragment key={String(key)}>
                            {typeof decorateTaskRow === 'function' ? decorateTaskRow(t, idx, rowInner) : rowInner}
                        </Fragment>
                    );
                })}
            </div>
        </BoardGroupedSubmissionCard>
    );
}
